import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { softReload, getKnownAppVersion, recordAppVersion } from '../lib/appRefresh';

// iter87 — Non-blocking banner that shows when a new service-worker version takes over.
//
// Triggers from two signals:
//   1. The SW posts `NEXFLOW_SW_UPDATED` to all clients after `activate` (push channel).
//   2. We ping the SW for its version on mount and compare against the last-known version
//      stored in localStorage (pull channel). Catches the case where a new SW activated
//      while the tab was closed.
//
// Tapping "Refresh Now" calls `softReload()` — no cache clearing, no draft loss. The new
// hashed bundle is fetched on the next request because the SW has already cleared the
// previous CACHE.

const STORAGE_DISMISS_KEY = 'nexflow.update_banner_dismissed_for';

const UpdateBanner = () => {
  const [newVersion, setNewVersion] = useState(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'NEXFLOW_SW_UPDATED' && data.version) {
        handleNewVersion(data.version);
      } else if (data.type === 'NEXFLOW_SW_VERSION' && data.version) {
        const known = getKnownAppVersion();
        if (!known) {
          // First time — record but don't nag the user.
          recordAppVersion(data.version);
        } else if (known !== data.version) {
          handleNewVersion(data.version);
        }
      }
    };

    const handleNewVersion = (version) => {
      try {
        const dismissedFor = window.localStorage.getItem(STORAGE_DISMISS_KEY);
        if (dismissedFor === version) return;   // user already said "later" for this version
      } catch { /* */ }
      setNewVersion(version);
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    // Pull-channel: ask the active SW what version it is.
    const ask = () => {
      try {
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage('NEXFLOW_VERSION_PING');
        }
      } catch { /* */ }
    };
    ask();
    const onControllerChange = () => ask();
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    // Re-poll every ~5 min while the tab is open in case the user leaves it overnight
    // during a deploy.
    const interval = setInterval(ask, 5 * 60 * 1000);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      clearInterval(interval);
    };
  }, []);

  if (!newVersion) return null;

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_DISMISS_KEY, newVersion); } catch { /* */ }
    setNewVersion(null);
  };

  const refresh = () => {
    recordAppVersion(newVersion);
    softReload();
  };

  return (
    <div
      data-testid="update-banner"
      role="status"
      aria-live="polite"
      className="sticky top-0 z-40 w-full bg-[#D4AF37] text-[#0A0A0A] shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <RefreshCw className="w-4 h-4 shrink-0" />
          <span className="truncate">New NexFlow update available</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="update-banner-refresh"
            onClick={refresh}
            className="bg-[#0A0A0A] text-[#D4AF37] rounded-md px-3 py-1.5 text-xs font-bold hover:bg-[#141414] transition-colors"
          >
            Refresh Now
          </button>
          <button
            data-testid="update-banner-dismiss"
            onClick={dismiss}
            aria-label="Dismiss update banner"
            className="text-[#0A0A0A] hover:bg-[#0A0A0A]/10 rounded-md p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateBanner;
